import { formatHumanName, getExtensionValue, MedplumClient, resolveId } from "@medplum/core";
import {
  Appointment,
  HealthcareService,
  Patient,
  Reference,
  Schedule,
  Slot,
} from "@medplum/fhirtypes";

// eslint-disable-next-line no-restricted-imports -- TODO: migrate to date-fns (grandfathered legacy dayjs usage)
import dayjs from "dayjs";
// eslint-disable-next-line no-restricted-imports -- TODO: migrate to date-fns (grandfathered legacy dayjs usage)
import timezone from "dayjs/plugin/timezone";
// eslint-disable-next-line no-restricted-imports -- TODO: migrate to date-fns (grandfathered legacy dayjs usage)
import utc from "dayjs/plugin/utc";
import humanizeDuration from "humanize-duration";
import human from "humanparser";
import join from "lodash/join";

import { Extensions } from "@/fhir/enums/extensions";
import {
  AcuitySchedulingResponseAppointmentType,
  AcuitySchedulingResponseCalendarHours,
} from "@/types/acuityScheduling";
import { getCodeableConceptCode } from "@/utils/medplum";

import {
  AcuitySchedulingAppointment,
  AcuitySchedulingCalendarRegularHoursDay,
  AcuitySchedulingEvent,
} from "./types";

// eslint-disable-next-line import/no-named-as-default-member
dayjs.extend(utc);
// eslint-disable-next-line import/no-named-as-default-member
dayjs.extend(timezone);

// Map short day codes to display format
const dayMap: Record<string, string> = {
  mon: "M",
  tue: "T",
  wed: "W",
  thu: "TH",
  fri: "F",
  sat: "S",
  sun: "SU",
};

const shortEnglishHumanizer = humanizeDuration.humanizer({
  language: "shortEn",
  languages: {
    shortEn: {
      h: () => "hr",
      m: () => "min",
    },
  },
});

/**
 * Converts a UUID string to a numeric representation using a more robust hash function.
 * Uses djb2 hash algorithm with better distribution to reduce collision risk.
 * @param uuid - The UUID string to convert
 * @returns A numeric representation of the UUID
 */
function convertUuidToNumber(uuid: string): number {
  let hash = 5381; // djb2 hash initial value
  for (let i = 0; i < uuid.length; i++) {
    const char = uuid.charCodeAt(i);
    hash = (hash << 5) + hash + char; // hash * 33 + char
  }
  return Math.abs(hash);
}

/**
 * Parses a wall-clock time string as belonging to the given location timezone.
 * Callers format for display (`.local()`) or persistence (`.toISOString()` / `.toDate()`).
 */
function parseWallClockInTimezone(wallClockTime: string, locationTimezone: string) {
  return dayjs.tz(wallClockTime, locationTimezone);
}

/**
 * Calculates and formats a time range string for an appointment based on start time and duration.
 * @param selectedTime - The starting time of the appointment (ISO string with offset, or local-format string)
 * @param duration - The length of the appointment in minutes
 * @returns A formatted string showing the time range in the viewer's local timezone (e.g. "09:00 AM - 10:00 AM")
 */
function calculateAppointmentTimeRange(selectedTime: string, duration: number) {
  if (!selectedTime || !duration) {
    return "";
  }

  const selectedTimeMoment = dayjs(selectedTime);
  const appointmentEnd = selectedTimeMoment.add(duration, "minutes");

  return `${selectedTimeMoment.format("hh:mm A")} - ${appointmentEnd.format("hh:mm A")}`;
}

/**
 * Formats a given date into a human-readable string format.
 *
 * @param selectedDate - The date to be formatted
 * @returns A string representation of the date in the format "Month DD, YYYY"
 * @example
 * // returns "January 01, 2024"
 * displaySelectedDate(new Date('2024-01-01'))
 */
function displaySelectedDate(selectedDate: Date) {
  if (!selectedDate) {
    return "";
  }

  return dayjs(selectedDate).format("MMMM DD, YYYY");
}

/**
 * Extracts initials from a person's full name by taking the first letter of first and last name.
 * @param personName - The full name of the person
 * @returns A string containing the person's initials (first letter of first name + first letter of last name)
 * @throws {Error} If the name cannot be parsed into first and last name components
 * @example
 * getPersonInitials("John Doe") // Returns "JD"
 */
function getPersonInitials(personName: string) {
  const { firstName, lastName } = human.parseName(personName ?? "");

  return join([firstName![0], lastName[0]], "");
}

/**
 * Processes a single appointment and returns the base event data and group IDs.
 * @param medplumClient The Medplum client instance.
 * @param organizationID The ID of the organization.
 * @param appointment The appointment to process.
 * @param groupByProvider Flag indicating whether to group by provider.
 * @returns A promise that resolves to the base event and group IDs, or null if processing fails.
 */
async function processAppointment(
  medplumClient: MedplumClient,
  organizationID: string,
  appointment: Appointment,
  groupByProvider: boolean,
): Promise<{ baseEvent: Omit<AcuitySchedulingEvent, "group_id">; groupIds: number[] } | null> {
  try {
    const appointmentTypeID = getCodeableConceptCode(appointment.appointmentType);

    if (!appointmentTypeID) {
      return null;
    }

    const appointmentType = await medplumClient.readResource(
      "HealthcareService",
      appointmentTypeID,
    );

    if (!appointmentType || !appointment.slot?.length) {
      return null;
    }

    const kind = getAppointmentTypeCharacteristicValue(
      appointmentType,
      "appointment-type-kind",
      Extensions.AcuityAppointmentTypeKind,
    ) as "service" | "class";

    const color = getAppointmentTypeCharacteristicValue(
      appointmentType,
      "appointment-type-color",
      Extensions.AcuityAppointmentTypeColor,
    );

    const title = await formatAppointmentTitle(medplumClient, appointment, appointmentType);

    if (!appointment.id) {
      console.warn(`Appointment missing ID`);
      return null;
    }

    // Use appointment UUID directly instead of converting to numeric ID
    const baseEvent = {
      id: appointment.id,
      start: new Date(appointment.start!),
      end: new Date(appointment.end!),
      title,
      color,
      type: kind === "class" ? ("class" as const) : ("appointment" as const),
    };

    // Decide grouping keys based on the groupByProvider flag
    let groupIds: number[];

    if (groupByProvider) {
      groupIds =
        appointment.participant
          ?.filter((p) => p.actor?.reference?.includes("Practitioner"))
          .map((p) => resolveId(p.actor) as string)
          .map((providerId) => convertUuidToNumber(providerId)) ?? [];
    } else {
      // For non-provider grouping, get the schedule ID
      const slot = await medplumClient.readReference(appointment.slot[0] as Reference<Slot>);
      const schedule = await medplumClient.readReference(slot.schedule as Reference<Schedule>);
      const scheduleNumericId = convertUuidToNumber(schedule.id!);
      groupIds = [scheduleNumericId];
    }

    return { baseEvent, groupIds };
  } catch (error) {
    return null;
  }
}

/**
 * Converts appointments to calendar events, optionally grouped by provider.
 *
 * @param medplumClient The Medplum client instance.
 * @param organizationID The ID of the organization.
 * @param appointments The list of appointments to convert.
 * @param groupByProvider Flag indicating whether to group by provider.
 * @returns A promise that resolves to an array of AcuitySchedulingEvent objects.
 */
async function convertAppointmentsToCalendarEvents(
  medplumClient: MedplumClient,
  organizationID: string,
  appointments: Appointment[],
  groupByProvider: boolean = false,
): Promise<AcuitySchedulingEvent[]> {
  const batches = await Promise.all(
    appointments.map(async (appt) => {
      const data = await processAppointment(medplumClient, organizationID, appt, groupByProvider);
      return data ? data.groupIds.map((group_id) => ({ ...data.baseEvent, group_id })) : [];
    }),
  );
  return batches.flat();
}

/**
 * Formats the title of an appointment.
 * Combines the appointment's name and type into a formatted string, or returns "Untitled Appointment" if both are missing.
 * @param {MedplumClient} medplumClient - The Medplum client object.
 * @param {Appointment} appointment - The appointment object containing the name and type information.
 * @param {HealthcareService} appointmentType - The appointment type object containing the name and type information.
 * @returns A formatted string representing the appointment title.
 */
async function formatAppointmentTitle(
  medplumClient: MedplumClient,
  appointment: Appointment,
  appointmentType: HealthcareService,
): Promise<string> {
  const { name: appointmentTypeName } = appointmentType;

  const appointmentTypeKind = getAppointmentTypeCharacteristicValue(
    appointmentType,
    "appointment-type-kind",
    Extensions.AcuityAppointmentTypeKind,
  ) as "service" | "class";

  if (appointmentTypeKind === "class") {
    return appointmentTypeName!;
  }

  const titleItems: string[] = [];

  // Only add appointment type name if it exists
  if (appointmentTypeName) {
    titleItems.push(appointmentTypeName);
  }

  const patientParticipant = appointment.participant?.find((participant) =>
    participant.actor?.reference?.includes("Patient"),
  );

  if (patientParticipant) {
    const patient = await medplumClient.readReference(
      patientParticipant.actor as Reference<Patient>,
    );

    const patientName = formatHumanName(patient.name?.[0]);

    if (patientName) {
      titleItems.push(patientName);
    }
  }

  return titleItems.length > 0 ? titleItems.join(": ") : "Untitled Appointment";
}

/**
 * Converts an array of Acuity Scheduling appointments to an array of calendar event groups.
 * Maps each Acuity appointment to a calendar event group object with id and title.
 * @param appointments - An array of AcuitySchedulingAppointment objects to convert.
 * @returns An array of calendar event group objects.
 */
function convertAppointmentsToCalendarEventGroups(appointments: AcuitySchedulingAppointment[]) {
  return appointments.map((appointment) => ({
    id: appointment.calendarID,
    title: appointment.calendar,
  }));
}

/**
 * Formats the date title based on the provided view.
 * Returns a formatted date string according to the specified view format.
 * Uses "MMMM YYYY" as a default format if the view is not recognized.
 * @param view - The view type ('week', 'month', or 'groups').
 * @param selectedDate - The date to format.
 * @returns A formatted date string.
 */
function getDateTitleForView(view: string, selectedDate: Date) {
  const titleForView: Record<string, string> = {
    week: "MMMM YYYY",
    month: "MMMM YYYY",
    groups: "MMMM DD, YYYY",
  };

  const format = titleForView[view] || "MMMM YYYY";
  return dayjs(selectedDate).format(format);
}

/**
 * Formats an appointment type's duration in a human-readable format.
 * Converts the duration from minutes to a string like "1hr 30min".
 * @param durationInMinutes - The duration of the appointment type in minutes.
 * @returns A human-readable string representing the duration.
 */
function humanizeAppointmentTypeDuration(durationInMinutes: number) {
  const durationInMs = durationInMinutes * 60 * 1000; // duration comes in minutes, we need in milliseconds
  return shortEnglishHumanizer(durationInMs, { units: ["h", "m"] });
}

/**
 * Filters an array of appointment types to return only those of type "service".
 * This function helps separate service appointments from other types of appointments.
 * @param appointmentTypes - The array of appointment types to filter.
 * @returns A new array containing only the appointment types that are classified as "service".
 */
function getServiceAppointmentTypes(appointmentTypes: AcuitySchedulingResponseAppointmentType[]) {
  return appointmentTypes.filter(
    (appointmentType: AcuitySchedulingResponseAppointmentType) =>
      appointmentType.type === "service",
  );
}

/**
 * Filters an array of appointment types to return only those not of type "service".
 * This function helps separate non-service appointments from other types of appointments.
 * @param appointmentTypes - The array of appointment types to filter.
 * @returns A new array containing only the appointment types that are not classified as "service".
 */
function getNonServiceAppointmentTypes(
  appointmentTypes: AcuitySchedulingResponseAppointmentType[],
) {
  return appointmentTypes.filter(
    (appointmentType: AcuitySchedulingResponseAppointmentType) =>
      appointmentType.type !== "service",
  );
}

/**
 * Retrieves a class offering from an array of appointments based on the appointment ID and organization ID.
 *
 * @param {MedplumClient} medplumClient - The MedplumClient instance for making API requests.
 * @param {number} appointmentID - The ID of the appointment to match.
 * @param {string} organizationID - The ID of the organization.
 * @param {Appointment[]} appointments - The array of appointments to search.
 * @returns {Promise<Appointment | undefined>} The class offering appointment, if found.
 */
async function getClassOfferingFromAppointmentID(
  medplumClient: MedplumClient,
  appointmentID: string,
  organizationID: string,
  appointments: Appointment[],
): Promise<Appointment | undefined> {
  for (const classOffering of appointments) {
    const isCorrectAppointment = classOffering.id === appointmentID;

    if (!isCorrectAppointment) continue;

    const appointmentTypeID = getCodeableConceptCode(classOffering.appointmentType);
    if (!appointmentTypeID) continue;

    const appointmentType = await medplumClient.readResource(
      "HealthcareService",
      appointmentTypeID,
    );

    if (!appointmentType) continue;

    const appointmentTypeKind = getAppointmentTypeCharacteristicValue(
      appointmentType,
      "appointment-type-kind",
      Extensions.AcuityAppointmentTypeKind,
    );

    if (appointmentTypeKind === "class") {
      return classOffering;
    }
  }

  return undefined;
}

/**
 * Maps FHIR available time slots to Acuity Scheduling calendar hours.
 *
 * @param healthcareService - HealthcareService object
 * @returns AcuitySchedulingResponseCalendarHours object with mapped calendar hours
 */
function fhirAvailableTimeToAcuityCalendarHours(
  healthcareService: HealthcareService,
): AcuitySchedulingResponseCalendarHours {
  const { availableTime } = healthcareService;

  // Map FHIR daysOfWeek (lowercase 3-letter) to AcuitySchedulingCalendarRegularHoursDay (capitalized full)
  const dayMap: Record<string, AcuitySchedulingCalendarRegularHoursDay> = {
    mon: "Monday",
    tue: "Tuesday",
    wed: "Wednesday",
    thu: "Thursday",
    fri: "Friday",
    sat: "Saturday",
    sun: "Sunday",
  };

  // Initialize all days with empty array
  const result: AcuitySchedulingResponseCalendarHours = {
    Sunday: [],
    Monday: [],
    Tuesday: [],
    Wednesday: [],
    Thursday: [],
    Friday: [],
    Saturday: [],
  };

  for (const slot of availableTime ?? []) {
    const { daysOfWeek, availableStartTime, availableEndTime } = slot;
    if (!daysOfWeek || !availableStartTime || !availableEndTime) continue;
    for (const day of daysOfWeek) {
      const mappedDay = dayMap[day.toLowerCase()];
      if (mappedDay) {
        // Format as "09:00-17:00"
        const timeRange = `${availableStartTime.slice(0, 5)}-${availableEndTime.slice(0, 5)}`;
        result[mappedDay].push(timeRange);
      }
    }
  }

  return result;
}

/**
 * Retrieves the value of a specific characteristic for a healthcare service.
 *
 * @param service - The HealthcareService object to extract the characteristic value from
 * @param code - The code of the characteristic to retrieve
 * @param extensionUrl - The URL of the extension containing the characteristic value
 * @returns The value of the specified characteristic, or undefined if not found
 */
function getAppointmentTypeCharacteristicValue(
  service: HealthcareService | undefined,
  code: string,
  extensionUrl: string,
): string | undefined {
  // Guard against undefined/null service
  if (!service || !service.characteristic) {
    return undefined;
  }

  // Try to find with the new code first
  let codeableConcept = service.characteristic.find(
    (characteristic) => getCodeableConceptCode(characteristic) === code,
  );

  // Fallback: If not found and code doesn't start with "acuity-", try with "acuity-" prefix
  // This supports backward compatibility with existing resources
  if (!codeableConcept && !code.startsWith("acuity-")) {
    const legacyCode = `acuity-${code}`;
    codeableConcept = service.characteristic.find(
      (characteristic) => getCodeableConceptCode(characteristic) === legacyCode,
    );
  }

  const value = getExtensionValue(codeableConcept, extensionUrl);
  return value !== undefined && value !== null ? String(value) : undefined;
}

/**
 * Retrieves a reference from a participant.
 * @param appointment - The appointment object to search.
 * @param medplumClient - The Medplum client object.
 * @param participantType - The type of participant to search for.
 *
 * @returns The reference object, if found.
 */
async function getAppointmentParticipant(
  appointment: Appointment,
  medplumClient: MedplumClient,
  participantType: string,
) {
  const participant = appointment.participant?.find((participant) =>
    participant.actor?.reference?.includes(participantType),
  );
  if (!participant?.actor?.reference) return undefined;
  const [refType] = participant.actor.reference.split("/");
  if (refType !== participantType) return undefined;
  return medplumClient.readReference({ reference: participant.actor.reference });
}

export {
  dayMap,
  calculateAppointmentTimeRange,
  parseWallClockInTimezone,
  convertAppointmentsToCalendarEventGroups,
  convertAppointmentsToCalendarEvents,
  convertUuidToNumber,
  displaySelectedDate,
  formatAppointmentTitle,
  getClassOfferingFromAppointmentID,
  getDateTitleForView,
  getNonServiceAppointmentTypes,
  getPersonInitials,
  getServiceAppointmentTypes,
  humanizeAppointmentTypeDuration,
  fhirAvailableTimeToAcuityCalendarHours,
  getAppointmentTypeCharacteristicValue,
  getAppointmentParticipant,
};
